import { AssetType, JobType, type JobPriorityType, type JobStatusType } from '@/enum'
import type {
  IJobAssociatedAssetInfo,
  IJobAssociatedAssetUpdate,
  IJobAssociatedMaintenancePart,
  IJobAssociatedMaintenanceTypeUpdate,
  IJobAssociatedTaskSheetUpdate,
  IJobUpdate
} from '@/interfaces'
import type { IDocument } from '@/interfaces/IDocument'
import type { IValueBasedTrigger } from '@/interfaces/jobs/IValueBasedTrigger'
import { containsValues } from '@/utils/helpers/app-helpers'
import DocumentModel from '../DocumentModel'
import JobAssociatedAssetInfo from './JobAssociatedAssetInfo'
import JobAssociatedAssetUpdate from './JobAssociatedAssetUpdate'
import JobAssociatedMaintenancePart from './JobAssociatedMaintenancePart'
import JobAssociatedMaintenanceTypeUpdate from './JobAssociatedMaintenanceTypeUpdate'
import JobAssociatedTaskSheetUpdate from './JobAssociatedTaskSheetUpdate'
import { getStartOfDay, toUTCDate } from '@/utils/helpers'
import ValueBasedTrigger from './ValueBasedTrigger'
import JobAssignedEmployeeUpdate from './JobAssignedEmployeeUpdate'

class JobUpdate implements IJobUpdate {
  title: string

  description?: string

  type?: JobType

  status?: JobStatusType

  priority?: JobPriorityType

  odometerTrigger?: IValueBasedTrigger

  machineHoursTrigger?: IValueBasedTrigger

  scheduledAt?: string | null

  dueAt?: string | null

  asset?: IJobAssociatedAssetUpdate

  assetInfo?: IJobAssociatedAssetInfo

  assignedEmployees?: JobAssignedEmployeeUpdate[]

  issueReporterId?: string

  workOrderVendorId?: string

  workOrderUrl?: string

  maintenanceTypes: IJobAssociatedMaintenanceTypeUpdate[]

  maintenanceParts: IJobAssociatedMaintenancePart[]

  maintenanceIssueIds: string[]

  taskSheets?: IJobAssociatedTaskSheetUpdate[]

  documents?: IDocument[]

  notes?: string

  isEmployeeSafetyChecksCompleted?: boolean

  notifyOtherEmployees: boolean

  firstEmployee?: string

  secondEmployee?: string

  constructor(jobUpdate?: any) {
    this.title = jobUpdate?.title ?? ''
    this.description = jobUpdate?.description
    this.type = jobUpdate?.type ?? JobType.GENERAL
    this.status = jobUpdate?.status as number
    this.priority = jobUpdate?.priority
    this.dueAt = jobUpdate?.jobDueDate ? toUTCDate(jobUpdate?.jobDueDate) : null
    this.scheduledAt = jobUpdate?.jobStartDate
      ? toUTCDate(jobUpdate?.jobStartDate)
      : jobUpdate?.jobDueDate
        ? toUTCDate(getStartOfDay(jobUpdate?.jobDueDate).toISOString())
        : null

    if (jobUpdate?.asset?.id && jobUpdate.asset.type !== undefined && jobUpdate.asset.type !== null)
      this.asset = {
        id: jobUpdate.asset.id,
        type: jobUpdate.asset.type
      } as IJobAssociatedAssetUpdate

    if (jobUpdate?.asset?.id) {
      if (jobUpdate?.asset?.type === AssetType.VEHICLE) {
        this.assetInfo = new JobAssociatedAssetInfo({
          machineHours: jobUpdate?.asset?.vehicle?.machineHours,
          odometerReading: jobUpdate?.asset?.vehicle?.odometerReading
        })
      }

      if (jobUpdate?.asset?.type === AssetType.EQUIPMENT) {
        this.assetInfo = new JobAssociatedAssetInfo({
          machineHours: jobUpdate?.asset?.equipment?.machineHours
        })
      }

      if (jobUpdate?.asset?.type === AssetType.MACHINE) {
        this.assetInfo = new JobAssociatedAssetInfo({
          machineHours: jobUpdate?.asset?.machine?.machineHours,
          odometerReading: jobUpdate?.asset?.machine?.odometerReading
        })
      }
    }

    if (Array.isArray(jobUpdate.assignedEmployeeIds)) {
      this.assignedEmployees = jobUpdate?.assignedEmployeeIds?.map((id: string, idx: number) => {
        return new JobAssignedEmployeeUpdate({
          id: id,
          order: jobUpdate?.assignedEmployeeIds.length == 1 ? -1 : (idx + 1)
        });
      })
    } else {
      this.assignedEmployees = [
        new JobAssignedEmployeeUpdate({
          id: jobUpdate?.assignedEmployeeIds,
          order: -1
        })
      ]
    }

    this.issueReporterId = jobUpdate?.issueReporterId
    this.workOrderVendorId = jobUpdate?.workOrderVendorId
    this.workOrderUrl = jobUpdate?.workOrderUrl

    this.maintenanceTypes = jobUpdate?.maintenanceTypes?.map(
      (maintenanceType: any) => new JobAssociatedMaintenanceTypeUpdate(maintenanceType)
    )
    this.maintenanceParts = jobUpdate?.maintenanceParts?.map(
      (maintenancePart: any) => new JobAssociatedMaintenancePart(maintenancePart)
    )
    this.maintenanceIssueIds = jobUpdate?.maintenanceIssueIds

    if (jobUpdate?.odometerTrigger?.value > 0) {
      this.odometerTrigger = new ValueBasedTrigger(jobUpdate?.odometerTrigger)
    } else {
      this.odometerTrigger = null as any
    }

    if (jobUpdate?.machineHoursTrigger?.value > 0) {
      this.machineHoursTrigger = new ValueBasedTrigger(jobUpdate?.machineHoursTrigger)
    } else {
      this.machineHoursTrigger = null as any
    }

    this.taskSheets =
      jobUpdate?.taskSheets
        ?.filter((taskSheet: any) => !!taskSheet.title)
        .map((taskSheet: any) => new JobAssociatedTaskSheetUpdate(taskSheet)) ?? []

    this.documents = jobUpdate?.documents?.map((document: any) => new DocumentModel(document))
    this.isEmployeeSafetyChecksCompleted = jobUpdate?.isEmployeeSafetyChecksCompleted
    this.notes = jobUpdate?.notes

    if (!jobUpdate.isMachineHoursBasedSelected) {
      this.machineHoursTrigger = null as any
    }

    if (!jobUpdate.isOdometerBasedSelected) {
      this.odometerTrigger = null as any
    }

    if (
      !jobUpdate.isTimeBasedSelected &&
      jobUpdate.type !== JobType.GENERAL &&
      jobUpdate.type !== JobType.ISSUE &&
      jobUpdate.type !== JobType.WORK_ORDER &&
      jobUpdate.type !== JobType.MILL_ORDER
    ) {
      this.scheduledAt = null
      this.dueAt = null
    }
    this.notifyOtherEmployees = true

    this.firstEmployee = jobUpdate?.firstEmployee ?? null
    this.secondEmployee = jobUpdate?.secondEmployee ?? null

    if (jobUpdate.type == JobType.MILL_ORDER) {
      this.assignedEmployees = []
      
      this.assignedEmployees.push(new JobAssignedEmployeeUpdate({
        id: this.firstEmployee,
        order: 1
      }))

      this.assignedEmployees.push(new JobAssignedEmployeeUpdate({
        id: this.secondEmployee,
        order: 2
      }))
    }
  }
}

export default JobUpdate
